/**
 * DeviceModelPicker — searchable type-ahead for the device_model master.
 *
 * Server-filtered by gadget_category + category_type + brand + series. Shows
 * price (GBP) in results so customers immediately know what we have on file.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Loader2 } from "lucide-react";

export default function DeviceModelPicker({ category, categoryType, brand, series, value, onPick, testId = "device-model" }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const search = (text) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: 30 };
        if (text) params.q = text;
        if (brand) params.brand = brand;
        if (series) params.series = series;
        if (category) params.category = category;
        if (categoryType) params.category_type = categoryType;
        const r = await api.get("/masters/lookup/device_model", { params });
        setResults(r.data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    setOpen(true);
    search(text);
    if (!text) onPick({ label: "", data: {} });
  };

  const pick = (row) => {
    setQuery(row.label);
    onPick(row);
    setOpen(false);
  };

  const placeholder = brand ? `Search ${brand} models…` : "Search 10,000+ models…";

  return (
    <div ref={boxRef} className="relative mt-1.5">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          className="pl-9"
          value={query}
          onChange={handleChange}
          onFocus={() => { setOpen(true); search(query); }}
          placeholder={placeholder}
          data-testid={`${testId}-search-input`}
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
      </div>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-border bg-popover shadow-md"
          data-testid={`${testId}-results`}
        >
          {results.map((r) => {
            const d = r.data || {};
            const price = d.price_gbp;
            return (
              <button
                type="button"
                key={r.id}
                data-testid={`${testId}-opt-${r.label}`}
                onClick={() => pick(r)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors flex items-start justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{r.label}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[d.brand, d.series, d.release_year].filter(Boolean).join(" · ")}
                    {d.model_numbers ? <span className="ml-1 font-mono">· {String(d.model_numbers).split(",")[0].trim()}</span> : null}
                  </div>
                </div>
                {price ? (
                  <span className="shrink-0 text-xs font-medium text-primary tabular-nums">£{Math.round(price)}</span>
                ) : null}
              </button>
            );
          })}
          {query && (
            <button
              type="button"
              data-testid={`${testId}-opt-custom`}
              onClick={() => pick({ label: query, data: {} })}
              className="w-full text-left px-3 py-2 text-sm border-t border-border text-primary hover:bg-accent transition-colors"
            >
              Use custom model: “{query}”
            </button>
          )}
          {!loading && results.length === 0 && !query && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Start typing to search…</div>
          )}
        </div>
      )}
    </div>
  );
}
